// Per-line allocation helpers, shared by the receipt modal and the ERA line
// matcher.
//
// Extracted from Receipt.jsx so the two screens cannot disagree about what a
// line still owes or whether an invoice may be paid line by line. The
// equivalents live server-side in
// backend/src/services/receiptAllocationService.js, and the two must stay in
// step: the server is authoritative and will refuse anything the client lets
// through, so a drift here shows up as a confusing 422 rather than a wrong
// number.

/** Money parser for text inputs: "R 1 234.56" becomes 1234.56. */
export const parseMoneyValue = (v) =>
  typeof v === "number"
    ? v
    : parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;

/**
 * Outstanding portion of a single invoice line for one payer side.
 *
 * Lines carry cumulative per-line paid tracking (patientPaid / medAidPaid) once
 * they have been receipted per line; older lines simply have no paid fields yet,
 * which reads as nothing paid.
 */
export const lineOutstanding = (line, source) => {
  const total =
    source === "patient" ? line?.patientAmount || 0 : line?.medAidAmount || 0;
  const paid =
    source === "patient" ? line?.patientPaid || 0 : line?.medAidPaid || 0;
  return Math.max(0, parseFloat((total - paid).toFixed(2)));
};

/**
 * Per-line allocation is only offered while the line-level paid tracking
 * accounts for every payment already on the invoice. An invoice-level receipt
 * updates outstandingBalance without stamping the lines, so the lines would
 * claim more owed than the invoice does, and allocating per line on top would
 * double-count the earlier payment. The server enforces the same rule.
 */
export const canPayPerLine = (inv) => {
  const lineTrackedPaid = (inv?.lines || []).reduce(
    (s, l) =>
      s + (parseFloat(l.patientPaid) || 0) + (parseFloat(l.medAidPaid) || 0),
    0
  );
  const invoicePaid = parseFloat(inv?.outstandingBalance?.paidAmount) || 0;
  return Math.abs(lineTrackedPaid - invoicePaid) <= 0.02;
};

export const PER_LINE_BLOCKED_MSG =
  "Earlier payments on this invoice were not allocated line by line. Allocate this payment at invoice level.";

/**
 * Clamp a typed money value to a ceiling, preserving what the user is part way
 * through typing. Returns the text to display, not a number, so an in-progress
 * "12." is not destroyed mid-keystroke.
 */
export const sanitiseMoneyInput = (value, max) => {
  const cleaned = String(value ?? "").replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return cleaned;
  const num = parseFloat(cleaned);
  if (Number.isNaN(num)) return "";
  if (typeof max === "number" && num > max) return String(max);
  return cleaned;
};

/** Currency for display. en-ZA per CLAUDE.md, used everywhere money is shown. */
export const zar = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
});
